import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Tags, Plus, ArrowLeft, Edit2, Trash2, TrendingUp, TrendingDown,
  X, Check, Folder, Wallet, Loader2, AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Toast, { useToast } from '../components/Toast';
import api from '../services/api';

interface Category {
  id: string;
  name: string;
  type: 'income' | 'expense';
  icon: string;
  color: string;
  description?: string;
  transactionCount?: number;
}

const colorOptions = [
  '#EF4444', '#F59E0B', '#10B981', '#36CCD8', '#3B82F6', 
  '#6366F1', '#8B5CF6', '#EC4899', '#6B7280', '#DC2626'
];

const iconOptions = ['🔧', '🛡️', '🌿', '🧹', '⚡', '💧', '📋', '🏠', '🎉', '🏦', '⚠️', '💰', '📦', '🚗', '🔨', '📝'];

export default function Categorias() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'income' | 'expense'>('expense');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { toasts, removeToast, success: toastSuccess, error: toastError } = useToast();

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState(colorOptions[0]);
  const [formIcon, setFormIcon] = useState(iconOptions[0]);

  const condoId = user?.condoId;

  const loadCategories = useCallback(async () => {
    if (!condoId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/financial/condo/${condoId}/categories`);
      const data = res.data?.data ?? res.data;
      const items = Array.isArray(data) ? data : data?.categories ?? data?.items ?? [];
      // Normalize: ensure each category has type field
      const normalized = items.map((c: any) => ({
        id: c.id,
        name: c.name,
        type: c.type || 'expense',
        icon: c.icon || '📦',
        color: c.color || '#6366F1',
        description: c.description || '',
        transactionCount: c.transaction_count || c.transactionCount || 0,
      }));
      setCategories(normalized);
    } catch (err: any) {
      console.error('Error loading categories:', err);
      setError('No se pudieron cargar las categorías');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const filteredCategories = categories.filter(c => c.type === activeTab);

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormColor(colorOptions[0]);
    setFormIcon(iconOptions[0]);
    setFormError('');
  };

  const openNewModal = () => {
    resetForm();
    setEditingCategory(null);
    setShowNewModal(true);
  };

  const openEditModal = (cat: Category) => {
    setFormName(cat.name);
    setFormDescription(cat.description || '');
    setFormColor(cat.color || colorOptions[0]);
    setFormIcon(cat.icon || '📦');
    setEditingCategory(cat);
    setFormError('');
    setShowNewModal(true);
  };

  const closeModal = () => {
    setShowNewModal(false);
    setEditingCategory(null);
    resetForm();
  };

  const handleSave = async () => {
    setFormError('');
    if (!formName.trim()) {
      setFormError('El nombre es obligatorio');
      return;
    }
    if (!condoId) {
      setFormError('No se ha asignado un condominio');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formName.trim(),
        type: activeTab,
        icon: formIcon,
        color: formColor,
        description: formDescription.trim() || undefined,
      };

      if (editingCategory) {
        // PUT to update
        await api.put(`/financial/condo/${condoId}/categories/${editingCategory.id}`, payload);
      } else {
        // POST to create
        await api.post(`/financial/condo/${condoId}/categories`, payload);
      }

      const wasEdit = !!editingCategory;
      await loadCategories();
      closeModal();
      toastSuccess(wasEdit ? 'Categoría actualizada exitosamente' : 'Categoría creada exitosamente');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Error al guardar la categoría';
      setFormError(msg);
      toastError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (cat: Category) => {
    setConfirmDelete(cat);
  };

  const performDelete = async () => {
    if (!condoId || !confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/financial/condo/${condoId}/categories/${confirmDelete.id}`);
      setCategories(prev => prev.filter(c => c.id !== confirmDelete.id));
      toastSuccess('Categoría eliminada exitosamente');
      setConfirmDelete(null);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Error al eliminar';
      toastError(msg);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-full">
      <Loader2 className="w-8 h-8 animate-spin text-[#36CCD8]" />
    </div>
  );

  if (!condoId) return (
    <div className="flex items-center justify-center min-h-full">
      <div className="text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Wallet className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">Sin condominio asignado</h2>
        <p className="text-slate-500">Contacta al administrador para continuar.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-[#2D3748]">Categorías</h1>
          </div>
          {isAdmin() && (
            <button onClick={openNewModal} className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl flex items-center gap-2 hover:bg-[#2bb8c4] transition-colors">
              <Plus className="w-4 h-4" /> Nueva
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
            <button onClick={loadCategories} className="ml-auto text-sm font-medium underline">Reintentar</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button 
            onClick={() => setActiveTab('expense')}
            className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
              activeTab === 'expense' ? 'bg-red-500 text-white' : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <TrendingDown className="w-5 h-5" /> Egresos
          </button>
          <button 
            onClick={() => setActiveTab('income')}
            className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
              activeTab === 'income' ? 'bg-emerald-500 text-white' : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <TrendingUp className="w-5 h-5" /> Ingresos
          </button>
        </div>

        {/* Categories Grid */}
        {filteredCategories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Folder className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-gray-500 text-lg">No hay categorías de {activeTab === 'income' ? 'ingresos' : 'egresos'}</p>
            <p className="text-gray-400 text-sm mt-1">Crea la primera categoría con el botón "Nueva".</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filteredCategories.map(category => (
              <div key={category.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                      style={{ backgroundColor: (category.color || '#6366F1') + '20' }}
                    >
                      {category.icon || '📦'}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2D3748]">{category.name}</h3>
                      {category.transactionCount !== undefined && category.transactionCount > 0 && (
                        <p className="text-sm text-gray-500">{category.transactionCount} transacciones</p>
                      )}
                      {category.description && (
                        <p className="text-xs text-gray-400 mt-1">{category.description}</p>
                      )}
                    </div>
                  </div>
                  {isAdmin() && (
                    <div className="flex gap-1">
                      <button 
                        onClick={() => openEditModal(category)}
                        className="p-2 text-gray-400 hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(category)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {(showNewModal || editingCategory) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#2D3748] mb-6">{editingCategory ? 'Editar' : 'Nueva'} Categoría</h3>
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-1">Nombre *</label>
                <input 
                  type="text" 
                  placeholder="Nombre de la categoría" 
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#36CCD8]" 
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-1">Descripción (opcional)</label>
                <input 
                  type="text" 
                  placeholder="Descripción" 
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-[#36CCD8]" 
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Icono</label>
                <div className="flex flex-wrap gap-2">
                  {iconOptions.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormIcon(icon)}
                      className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center transition-all ${
                        formIcon === icon ? 'bg-[#36CCD8]/20 border-2 border-[#36CCD8]' : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2D3748] mb-2">Color</label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map(color => (
                    <button 
                      key={color} 
                      type="button"
                      onClick={() => setFormColor(color)}
                      className={`w-8 h-8 rounded-full border-2 shadow-sm hover:scale-110 transition-transform ${
                        formColor === color ? 'border-[#2D3748] ring-2 ring-[#36CCD8]' : 'border-white'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> {formError}
                </div>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="flex-1 py-3 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors">Cancelar</button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="flex-1 py-3 bg-[#36CCD8] text-white rounded-xl font-bold hover:bg-[#2bb8c4] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                  ) : (
                    <><Check className="w-4 h-4" /> Guardar</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmación de eliminación (diálogo en la app) */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-[#2D3748] text-center mb-2">Eliminar categoría</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              ¿Eliminar la categoría «{confirmDelete.name}»? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={performDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Eliminando...</>) : (<><Trash2 className="w-4 h-4" /> Eliminar</>)}
              </button>
            </div>
          </div>
        </div>
      )}
      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
